/*
    ButtonLink is a a utility that creates a link that looks like a button.
    Veneer's standard button component does not allow changing the root
    element's tag name. Wrapping it in an <a> adds an accessibility issue:
    cycling through focusable elements with the tab key will result in the
    button/link combo requiring *two* presses.

    This wrapper solves the issues by

    1) setting tabindex="-1" on the link, so it doesn't count in the
       document's tab order
    2) adding a utility class to remove hover/focus decoration from the link
       (let the button element control these)
    3) adding the "presentation" ARIA role to the button so screen readers
       should only announce the link
*/
import {css, cx} from '@emotion/css';
import React from 'react';

import {Button, ButtonInterface} from '@veneer/core';

import A from './Link';

export interface ButtonLinkInterface extends ButtonInterface {
    href: string;
    download?: boolean;
    target?: string;
    rel?: string;
    outerClassName?: string;
    disabled?: boolean;
    ariaLabel?: string;
    forceRedirect?: boolean;
}

const ButtonLink = ({
    href,
    download,
    target,
    rel,
    outerClassName,
    disabled = false,
    ariaLabel,
    forceRedirect = false,
    ...buttonProps
}: ButtonLinkInterface): JSX.Element => {
    const linkProps = {
        className: cx([
            css`
                /*
            De-style any links that contain buttons. The hover/active/focus state
            is handled by the button.
            */
                &,
                &:hover,
                &:active,
                &:focus {
                    font: inherit;
                    text-decoration: none;
                    outline: none;
                }
            `,
            outerClassName,
        ]),
        // Negative tabindex stops tabbing to this element; it has no
        // effect on child elements so the wrapped button will be next in
        // the tab order.
        tabIndex: -1,
        to: href,
        href: href,
        download: download,
        target: target,
        rel: rel,
        'aria-label': ariaLabel,
    };

    if (disabled) {
        return (
            <Button
                aria-label={ariaLabel}
                disabled
                {...buttonProps}
            />
        );
    }

    return (
        <A
            {...linkProps}
            // eslint-disable-next-line react/forbid-component-props
            forceRedirect={forceRedirect}
            download={download}
        >
            {/*  eslint-disable-next-line no-restricted-syntax */}
            <Button
                // role="presentation" prevents announcement as a button by
                // screen readers
                role="presentation"
                {...buttonProps}
            />
        </A>
    );
};

export default ButtonLink;
